/**
 * showdownMoves — the first batch of per-move Showdown animation scripts, plus
 * the resolver that maps one of our move ids to a {@link MoveAnim}.
 *
 * Each entry in {@link SHOWDOWN_MOVE_ANIMS} is a near-verbatim port of the move's
 * script from Pokémon Showdown's `BattleMoveAnims` (`battle-animations-moves.ts`,
 * MIT/CC0). Our {@link FxScene} + {@link Actor} mirror Showdown's `scene` +
 * `Sprite`, so the literal `scene.showEffect(...)` / `attacker.anim(...)` /
 * `scene.wait(...)` sequences port unchanged. Where a move pointed at a shared
 * template, we reuse the ported template from {@link module:showdownOther}.
 *
 * Effect-name discipline: only effect keys vendored in our fx catalog are used
 * (see client/data/generated/fx/catalog.json). Where Showdown referenced an
 * effect/background we did not vendor (sprite-as-effect, bespoke claw/stare PNGs,
 * `scene.$bg.animate`, remote-url backgrounds), the call is adapted to the nearest
 * vendored effect, substituted with a `backgroundEffect` colour wash, or omitted —
 * noted inline at each site. The motion (the `actor.anim` choreography) is always
 * preserved verbatim.
 *
 * Test/runtime separation: this module imports ONLY TYPES from `showdownFx.ts`
 * (that file pulls in Phaser, which can't load in the node test env). Every script
 * here is a pure function over the `FxScene`/`Actor` interfaces, so `getShowdownAnim`
 * resolves to plain functions and is unit-testable in node without Phaser.
 */
import type { MoveCategory } from '@/shared';
import type { FxScene, Actor, MoveAnim, MoveAnimEntry } from './showdownFx.js';
import {
  attack,
  contactattack,
  slashattack,
  punchattack,
  bite as biteTemplate,
  fastattack,
  fastanimself,
} from './showdownOther.js';
import { SHOWDOWN_BATCH_0 } from './showdownMoves.batch0.js';
import { SHOWDOWN_BATCH_1 } from './showdownMoves.batch1.js';
import { SHOWDOWN_BATCH_2 } from './showdownMoves.batch2.js';
import { SHOWDOWN_BATCH_3 } from './showdownMoves.batch3.js';
import { SHOWDOWN_BATCH_4 } from './showdownMoves.batch4.js';
import { SHOWDOWN_BATCH_5 } from './showdownMoves.batch5.js';
import { SHOWDOWN_BATCH_6 } from './showdownMoves.batch6.js';
import { SHOWDOWN_BATCH_7 } from './showdownMoves.batch7.js';
import { SHOWDOWN_BATCH_8 } from './showdownMoves.batch8.js';
import { SHOWDOWN_BATCH_9 } from './showdownMoves.batch9.js';

/* ──────────────────────────────────────────────────────────────────────────
 * Local template ports used by several scripts (PS `drain`, `flight`).
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `BattleOtherAnims.drain` — three energy orbs siphon defender → attacker. */
const drain: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'energyball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 0.6, time: 0 },
    { x: attacker.x, y: attacker.y, z: attacker.z, time: 500, opacity: 0 },
    'ballistic2',
  );
  scene.showEffect(
    'energyball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 0.6, time: 50 },
    { x: attacker.x, y: attacker.y, z: attacker.z, time: 550, opacity: 0 },
    'linear',
  );
  scene.showEffect(
    'energyball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 0.6, time: 100 },
    { x: attacker.x, y: attacker.y, z: attacker.z, time: 600, opacity: 0 },
    'ballistic2Under',
  );
};

/** PS `BattleOtherAnims.flight` — fly off-screen, swoop back through the defender. */
const flight: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: attacker.leftof(-200), y: attacker.y + 80, z: attacker.z, opacity: 0, time: 350 },
    'accel',
  );
  attacker.anim({ x: defender.leftof(-200), y: defender.y + 80, z: defender.z, time: 1 }, 'linear');
  attacker.anim({ x: defender.x, y: defender.y, z: defender.z, opacity: 1, time: 350 }, 'accel');
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 700 },
    { scale: 2, opacity: 0, time: 900 },
    'linear',
  );
  attacker.anim({
    x: defender.leftof(100),
    y: defender.y - 40,
    z: defender.z,
    opacity: 0,
    time: 175,
  });
  attacker.anim({ x: attacker.x, y: attacker.y + 40, z: attacker.behind(40), time: 1 });
  attacker.anim({ x: attacker.x, y: attacker.y, z: attacker.z, time: 250 }, 'decel');
  defender.delay(700);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/* ──────────────────────────────────────────────────────────────────────────
 * Custom move scripts (the first batch).
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `thunderbolt` — three lightning bolts crash onto the defender. */
const thunderbolt: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.backgroundEffect('#000000', 600, 0.2);
  scene.showEffect(
    'lightning',
    { x: defender.x, y: defender.y + 150, z: defender.z, yscale: 0, xscale: 2 },
    { y: defender.y + 50, yscale: 1, xscale: 1.5, opacity: 0.8, time: 200 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'lightning',
    { x: defender.x - 15, y: defender.y + 150, z: defender.z, yscale: 0, xscale: 2, time: 200 },
    { y: defender.y + 50, yscale: 1, xscale: 1.5, opacity: 0.8, time: 400 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'lightning',
    { x: defender.x + 15, y: defender.y + 150, z: defender.z, yscale: 0, xscale: 2, time: 400 },
    { y: defender.y + 50, yscale: 1, xscale: 1.5, opacity: 0.8, time: 600 },
    'linear',
    'fade',
  );
};

/** PS `thunder` — a lightning strike, electro splash, and a violent shudder. */
const thunder: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.backgroundEffect('#ffffff', 300, 0.7);
  scene.backgroundEffect('#000000', 1000, 0.7, 100);
  scene.showEffect(
    'lightning',
    { x: defender.x, y: defender.y + 150, z: defender.z, yscale: 0, xscale: 2 },
    { y: defender.y + 50, yscale: 1, xscale: 1.5, opacity: 0, time: 200 },
    'linear',
  );
  scene.showEffect(
    'lightning',
    { x: defender.x, y: defender.y + 50, z: defender.z, yscale: 1, xscale: 1.5, time: 200 },
    { opacity: 0, time: 700 },
    'linear',
  );
  scene.showEffect(
    'lightning',
    { x: defender.x, y: defender.y + 50, z: defender.z, yscale: 1, xscale: 1.5, time: 600 },
    { opacity: 0, time: 1100 },
    'linear',
  );
  scene.showEffect(
    'electroball',
    {
      x: defender.x,
      y: defender.y - 60,
      z: defender.z,
      scale: 1,
      xscale: 1.5,
      opacity: 0.5,
      time: 200,
    },
    { scale: 2, xscale: 4, opacity: 0.1, time: 900 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y - 30, z: defender.z, opacity: 0.5, scale: 1.5, time: 200 },
    { scale: 1.8, opacity: 0.1, time: 900 },
    'linear',
    'fade',
  );
  defender.delay(200);
  defender.anim({ x: defender.x - 5, time: 75 }, 'swing');
  defender.anim({ x: defender.x + 5, time: 75 }, 'swing');
  defender.anim({ x: defender.x - 5, time: 75 }, 'swing');
  defender.anim({ x: defender.x + 5, time: 75 }, 'swing');
  defender.anim({ x: defender.x - 5, time: 75 }, 'swing');
  defender.anim({ time: 100 }, 'accel');
};

/** PS `flamethrower` — four fireballs spray onto the defender and burst. */
const flamethrower: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.7 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.6, time: 400 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.7, time: 100 },
    { x: defender.x + 10, y: defender.y - 5, z: defender.behind(0), opacity: 0.6, time: 500 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.7, time: 200 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(0), opacity: 0.6, time: 600 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.7, time: 300 },
    { x: defender.x, y: defender.y - 5, z: defender.behind(0), opacity: 0.6, time: 700 },
    'decel',
    'explode',
  );
};

/** PS `icebeam` — an icicle beam, two iceball bursts, and frosty wisps. */
const icebeam: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  const xstep = (defender.x - attacker.x) / 5;
  const ystep = (defender.y - attacker.y) / 5;
  const zstep = (defender.z - attacker.z) / 5;
  for (let i = 0; i < 4; i++) {
    scene.showEffect(
      'icicle',
      {
        x: attacker.x + xstep * (i + 1),
        y: attacker.y + ystep * (i + 1),
        z: attacker.z + zstep * (i + 1),
        scale: 1.5,
        opacity: 0.6,
        time: 40 * i,
      },
      { opacity: 0, time: 40 * i + 600 },
      'linear',
    );
  }
  scene.showEffect(
    'iceball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 100 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 0, time: 400 },
    'linear',
  );
  scene.showEffect(
    'iceball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 300 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x - 30, y: defender.y, z: defender.z, scale: 2, opacity: 0.5, time: 200 },
    { scale: 4, opacity: 0, time: 600 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y - 30, z: defender.z, scale: 2, opacity: 0.5, time: 300 },
    { scale: 4, opacity: 0, time: 650 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x + 15, y: defender.y, z: defender.z, scale: 2, opacity: 0.5, time: 400 },
    { scale: 4, opacity: 0, time: 700 },
    'linear',
    'fade',
  );
};

/** PS `blizzard` — four icicles whip across the defender and shatter. */
const blizzard: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#009AA4', 700, 0.5);
  scene.showEffect(
    'icicle',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6 },
    { x: defender.x + 60, y: defender.y + 40, z: defender.z, scale: 2, opacity: 0.3 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'icicle',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 75 },
    { x: defender.x + 40, y: defender.y - 40, z: defender.z, scale: 2, opacity: 0.3 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'icicle',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 150 },
    { x: defender.x - 60, y: defender.y, z: defender.z, scale: 2, opacity: 0.3 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'icicle',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 225 },
    { x: defender.x - 20, y: defender.y + 10, z: defender.z, scale: 2, opacity: 0.3 },
    'accel',
    'explode',
  );
};

/** PS `BattleOtherAnims.hydroshot` — three water wisps spray onto the defender. */
const hydroshot: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3 },
    { x: defender.x + 10, y: defender.y + 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3, time: 75 },
    { x: defender.x - 10, y: defender.y - 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3, time: 150 },
    { x: defender.x, y: defender.y + 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
};

/** PS `hydropump` — a blue wash + hydroshot, then the defender is shoved back. */
const hydropump: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.backgroundEffect('#0000DD', 700, 0.2);
  hydroshot(scene, actors);
  defender.delay(200);
  defender.anim({ z: defender.behind(20), time: 400 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `surf` — a blue wave washes over the defender, who rocks with the swell. */
const surf: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  defender.delay(125);
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');

  scene.backgroundEffect('#0000DD', 700, 0.2);
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y - 25, z: attacker.z, scale: 0.4, opacity: 0.3 },
    { x: defender.x, y: defender.y + 10, z: defender.behind(50), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x - 30, y: attacker.y - 25, z: attacker.z, scale: 0.4, opacity: 0.3 },
    { x: defender.x - 60, y: defender.y, z: defender.behind(50), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x + 30, y: attacker.y - 25, z: attacker.z, scale: 0.4, opacity: 0.3 },
    { x: defender.x + 60, y: defender.y, z: defender.behind(50), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
};

/** PS `bubblebeam` — four icy bubbles arc onto the defender and pop. */
const bubblebeam: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.7 },
    { x: defender.x, y: defender.y, z: defender.behind(0), opacity: 0.6, time: 400 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.7, time: 100 },
    { x: defender.x + 20, y: defender.y - 10, z: defender.behind(0), opacity: 0.6, time: 500 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.7, time: 200 },
    { x: defender.x - 20, y: defender.y + 10, z: defender.behind(0), opacity: 0.6, time: 600 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.7, time: 300 },
    { x: defender.x, y: defender.y - 5, z: defender.behind(0), opacity: 0.6, time: 700 },
    'decel',
    'explode',
  );
};

/** PS `quickattack` → `fastattack` template. */
const quickattack: MoveAnim = fastattack;

/** PS `scratch` → `slashattack` template. */
const scratch: MoveAnim = slashattack;

/** PS `pound` → `contactattack` template. */
const pound: MoveAnim = contactattack;

/** PS `bodyslam` — a heavy ballistic flop that squashes the defender flat. */
const bodyslam: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y - 30, z: defender.z, scale: 1, time: 500 },
    { x: defender.x + 70, scale: 0.8, opacity: 0.3, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y - 30, z: defender.z, scale: 1, time: 500 },
    { x: defender.x - 70, scale: 0.8, opacity: 0.3, time: 800 },
    'linear',
    'fade',
  );
  attacker.anim(
    { x: defender.x, y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.x, y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 600 }, 'ballistic2Back');
  defender.delay(450);
  defender.anim({ y: defender.y - 30, z: defender.behind(20), yscale: 0.5, time: 200 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `gigadrain` — a green wash + the energy-siphon drain template. */
const gigadrain: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  scene.backgroundEffect('#9AB440', 900, 0.5);
  drain(scene, actors);
};

/** PS `psychic` — a purple double-wash; the defender pulses larger and smaller. */
const psychic: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.backgroundEffect('#AA44BB', 250, 0.6);
  scene.backgroundEffect('#AA44FF', 250, 0.6, 400);
  defender.anim({ scale: 1.2, time: 100 });
  defender.anim({ scale: 1, time: 100 });
  defender.anim({ scale: 1.4, time: 150 });
  defender.anim({ scale: 1, time: 150 });
  scene.wait(700);
};

/** PS `shadowball` — wisps gather a shadow orb at the user, then hurl it. */
const shadowball: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#000000', 1000, 0.1);
  scene.showEffect(
    'poisonwisp',
    {
      x: attacker.x,
      y: attacker.y + 100,
      z: attacker.behind(-20),
      scale: 0.5,
      opacity: 0,
      time: 0,
    },
    { x: attacker.x, y: attacker.y, scale: 1, opacity: 0.8, time: 200 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    {
      x: attacker.x - 60,
      y: attacker.y - 80,
      z: attacker.behind(-20),
      scale: 0.5,
      opacity: 0,
      time: 50,
    },
    { x: attacker.x, y: attacker.y, scale: 1, opacity: 0.8, time: 300 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    {
      x: attacker.x + 60,
      y: attacker.y - 80,
      z: attacker.behind(-20),
      scale: 0.5,
      opacity: 0,
      time: 100,
    },
    { x: attacker.x, y: attacker.y, scale: 1, opacity: 0.8, time: 400 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    {
      x: attacker.x - 90,
      y: attacker.y + 40,
      z: attacker.behind(-20),
      scale: 0.5,
      opacity: 0,
      time: 150,
    },
    { x: attacker.x, y: attacker.y, scale: 1, opacity: 0.8, time: 500 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    {
      x: attacker.x + 90,
      y: attacker.y + 40,
      z: attacker.behind(-20),
      scale: 0.5,
      opacity: 0,
      time: 200,
    },
    { x: attacker.x, y: attacker.y, scale: 1, opacity: 0.8, time: 600 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'shadowball',
    { x: attacker.x, y: attacker.y, z: attacker.behind(-20), scale: 0, opacity: 0, time: 0 },
    { scale: 0.8, opacity: 0.5, time: 600 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.behind(-20), scale: 0, opacity: 0, time: 0 },
    { scale: 1.5, opacity: 0.8, time: 600 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'shadowball',
    { x: attacker.x, y: attacker.y, z: attacker.behind(-20), scale: 0.8, opacity: 0.8, time: 600 },
    { x: defender.x, y: defender.y, z: defender.z, time: 900 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.behind(-20), scale: 1.5, opacity: 0.8, time: 600 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, time: 900 },
    'accel',
    'explode',
  );
  defender.delay(900);
  defender.anim({ z: defender.behind(10), time: 200 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `sludgebomb` — three globs of poison lob onto the defender and splatter. */
const sludgebomb: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.7, opacity: 1, time: 400 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 100 },
    { x: defender.x + 40, y: defender.y - 20, z: defender.z, scale: 0.7, opacity: 1, time: 500 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 200 },
    { x: defender.x - 30, y: defender.y - 10, z: defender.z, scale: 0.7, opacity: 1, time: 600 },
    'ballistic',
    'explode',
  );
};

/**
 * PS `earthquake` — the whole field judders. Showdown shook the background via
 * `scene.$bg.animate({top,bottom}, …)`, which our engine has no equivalent for;
 * we substitute a short brown `backgroundEffect` rumble wash and keep the verbatim
 * attacker/defender bobbing + the rocks tossed up around the defender.
 */
const earthquake: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  // Substitute for PS's $bg shake — a brief brown ground-rumble wash.
  scene.backgroundEffect('#8a6a3a', 1100, 0.18);

  attacker.anim({ y: attacker.y - 10, yscale: 1, time: 75 });
  attacker.anim({ y: attacker.y + 10, yscale: 0.9, time: 100 });
  attacker.anim({ y: attacker.y - 7, yscale: 1, time: 100 });
  attacker.anim({ y: attacker.y + 7, time: 100 });
  attacker.anim({ y: attacker.y - 7, time: 100 });
  attacker.anim({ y: attacker.y + 7, time: 100 });
  attacker.anim({ y: attacker.y - 7, time: 100 });
  attacker.anim({ y: attacker.y + 7, time: 100 });
  attacker.anim({ y: attacker.y - 2, time: 100 });
  attacker.anim({ y: attacker.y + 2, time: 100 });
  attacker.anim({ y: attacker.y, time: 100 });

  defender.anim({ y: defender.y - 10, time: 75 });
  defender.anim({ y: defender.y + 10, time: 100 });
  defender.anim({ y: defender.y - 7, time: 100 });
  defender.anim({ y: defender.y + 7, time: 100 });
  defender.anim({ y: defender.y - 7, time: 100 });
  defender.anim({ y: defender.y + 7, time: 100 });
  defender.anim({ y: defender.y - 7, time: 100 });
  defender.anim({ y: defender.y + 7, time: 100 });
  defender.anim({ y: defender.y - 2, time: 100 });
  defender.anim({ y: defender.y + 2, time: 100 });
  defender.anim({ y: defender.y, time: 100 });

  scene.showEffect(
    'rock3',
    { x: defender.x + 5, y: defender.y - 35, z: defender.z, scale: 0.2, opacity: 1, time: 0 },
    { x: defender.x + 30, y: defender.y, scale: 0.4, opacity: 0, time: 350 },
    'ballistic',
  );
  scene.showEffect(
    'rock3',
    { x: defender.x - 10, y: defender.y - 35, z: defender.z, scale: 0.2, opacity: 1, time: 250 },
    { x: defender.x - 35, y: defender.y, scale: 0.3, opacity: 0, time: 600 },
    'ballistic',
  );
  scene.showEffect(
    'rock3',
    { x: defender.x + 40, y: defender.y - 35, z: defender.z, scale: 0.2, opacity: 1, time: 400 },
    { x: defender.x + 65, y: defender.y, scale: 0.3, opacity: 0, time: 750 },
    'ballistic2',
  );
  scene.showEffect(
    'rock3',
    { x: defender.x, y: defender.y - 35, z: defender.z, scale: 0.3, opacity: 1, time: 500 },
    { x: defender.x + 40, y: defender.y, scale: 0.4, opacity: 0, time: 750 },
    'ballistic',
  );
};

/** PS `rockslide` — boulders rain onto the defender, who flinches under each. */
const rockslide: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  defender.delay(200);
  defender.anim({ y: defender.y - 7, yscale: 0.9, time: 100 }, 'decel');
  defender.anim({ time: 200 });
  defender.delay(200);
  defender.anim({ y: defender.y - 7, yscale: 0.9, time: 100 }, 'decel');
  defender.anim({ time: 200 });

  scene.showEffect(
    'rock1',
    { x: defender.x + 15, y: defender.y + 100, z: defender.z, opacity: 0, scale: 0.5 },
    { y: defender.y - 30, opacity: 1, time: 300 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'rock2',
    { x: defender.x + 30, y: defender.y + 100, z: defender.z, opacity: 0, scale: 0.5, time: 100 },
    { y: defender.y - 30, opacity: 1, time: 400 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'rock1',
    { x: defender.x - 30, y: defender.y + 100, z: defender.z, opacity: 0, scale: 0.5, time: 200 },
    { y: defender.y - 30, opacity: 1, time: 500 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'rock2',
    { x: defender.x, y: defender.y + 100, z: defender.z, opacity: 0, scale: 0.5, time: 300 },
    { y: defender.y - 30, opacity: 1, time: 600 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'rock1',
    { x: defender.x - 15, y: defender.y + 100, z: defender.z, opacity: 0, scale: 0.5, time: 400 },
    { y: defender.y - 30, opacity: 1, time: 700 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'mudwisp',
    { x: defender.x + 40, y: defender.y - 40, z: defender.z, scale: 0, opacity: 0.4, time: 300 },
    { scale: 2, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'mudwisp',
    { x: defender.x - 40, y: defender.y - 40, z: defender.z, scale: 0, opacity: 0.4, time: 450 },
    { scale: 2, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'mudwisp',
    { x: defender.x + 10, y: defender.y - 40, z: defender.z, scale: 0, opacity: 0.4, time: 600 },
    { scale: 2, opacity: 0 },
    'decel',
  );
};

/**
 * PS `hyperbeam` — a devastating beam: a dark-field flash, then a full barrage of
 * electro orbs that scream attacker→defender (staggered, growing as they go), and a
 * pair of big shockwave bursts on impact.
 *
 * Faithful to Showdown's script (battle-animations-moves.ts `hyperbeam`): same dark
 * `backgroundEffect`, same six staggered electroball streaks (same target spread +
 * timing), same two impact shockwaves, same recoiling defender judder. But PS's raw
 * numbers (orbs at scale 0.4→0.6 / opacity 0.6→0.3, dark `shadowball` bursts fading
 * to 0) read tiny and faint once mapped onto our field at U=1 — so this port keeps
 * the choreography but lands it HARD: a white pre-flash under the dark wash, the orbs
 * fire bright and grow large (≈1.0→2.0) at full opacity, and the impact uses bright
 * `electroball` shockwaves (plus a white impact pop) instead of the muddy shadowball,
 * so the beam reads as the catastrophe it is. (Local to this script — the shared field
 * scale is untouched, so Thunderbolt/Flamethrower/Ice Beam are unaffected.)
 */
const hyperbeam: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  // The signature dark-field flash: a quick white pop, then the dramatic black wash.
  scene.backgroundEffect('#ffffff', 250, 0.5);
  scene.backgroundEffect('#000000', 700, 0.45, 80);

  // Six electro orbs streak attacker→defender, staggered, growing big and bright.
  // (PS spread/timing preserved; scale & opacity pumped so each orb reads BIG.)
  const orbs: Array<{ dx: number; dy: number; t0: number; t1: number }> = [
    { dx: 30, dy: 30, t0: 0, t1: 200 },
    { dx: 20, dy: -30, t0: 75, t1: 275 },
    { dx: -30, dy: 0, t0: 150, t1: 350 },
    { dx: -10, dy: 10, t0: 225, t1: 425 },
    { dx: 10, dy: -10, t0: 300, t1: 500 },
    { dx: -20, dy: 0, t0: 375, t1: 575 },
  ];
  for (const o of orbs) {
    scene.showEffect(
      'electroball',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.0, opacity: 0.9, time: o.t0 },
      {
        x: defender.x + o.dx,
        y: defender.y + o.dy,
        z: defender.z,
        scale: 2.0,
        opacity: 0.7,
        time: o.t1,
      },
      'linear',
      'explode',
    );
  }

  // Twin shockwave bursts on impact — bright + large, the beam's catastrophic landing.
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 540 },
    { scale: 6, opacity: 0, time: 760 },
    'decel',
  );
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.9, time: 600 },
    { scale: 6, opacity: 0, time: 820 },
    'decel',
  );
  // A white impact pop punches through the dark wash at the moment of landing.
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 0, time: 560 },
    { scale: 2.4, opacity: 1, time: 680 },
    'decel',
    'fade',
  );

  defender.delay(125);
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 150 }, 'swing');
};

/**
 * PS `solarbeam` — a sun-charged beam: an energy column rises off the user, then
 * a fan of fire/electro orbs streams onto the defender. The remote sunny-day
 * background image PS used is replaced with a warm yellow `backgroundEffect` wash.
 */
const solarbeam: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  const xstep = (defender.x - attacker.x) / 5;
  const ystep = (defender.x - 200 - attacker.x) / 5;
  const zstep = (defender.z - attacker.z) / 5;

  // PS used a remote weather-sunnyday.jpg here; substitute a warm sunlight wash.
  scene.backgroundEffect('#f6e08a', 900, 0.5);

  for (let i = 0; i < 5; i++) {
    scene.showEffect(
      'energyball',
      {
        x: attacker.x + xstep * (i + 1),
        y: attacker.y + 200 + ystep * (i + 1),
        z: attacker.z + zstep * (i + 1),
        scale: 0.7,
        opacity: 0.6,
        time: 40 * i + 300,
      },
      { opacity: 0, time: 100 * i + 500 },
      'linear',
    );
  }

  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.75, opacity: 0.6 },
    { x: attacker.x, y: attacker.y + 200, z: attacker.z, scale: 1.25, opacity: 0, time: 200 },
    'decel',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.6 },
    { x: attacker.x, y: attacker.y + 200, z: attacker.z, scale: 1.5, opacity: 0, time: 200 },
    'decel',
  );

  scene.showEffect(
    'flareball',
    { x: attacker.x, y: attacker.y + 200, z: attacker.z, scale: 0.4, opacity: 0.6, time: 300 },
    { x: defender.x + 30, y: defender.y + 30, z: defender.z, scale: 0.6, opacity: 0.3, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y + 200, z: attacker.z, scale: 0.4, opacity: 0.6, time: 375 },
    { x: defender.x + 20, y: defender.y - 30, z: defender.z, scale: 0.6, opacity: 0.3, time: 575 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y + 200, z: attacker.z, scale: 0.4, opacity: 0.6, time: 425 },
    { x: defender.x - 10, y: defender.y + 10, z: defender.z, scale: 0.6, opacity: 0.3, time: 625 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'flareball',
    { x: attacker.x, y: attacker.y + 200, z: attacker.z, scale: 0.4, opacity: 0.6, time: 450 },
    { x: defender.x - 30, y: defender.y, z: defender.z, scale: 0.6, opacity: 0.3, time: 650 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'flareball',
    { x: attacker.x, y: attacker.y + 200, z: attacker.z, scale: 0.4, opacity: 0.6, time: 500 },
    { x: defender.x + 10, y: defender.y - 10, z: defender.z, scale: 0.6, opacity: 0.3, time: 700 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y + 200, z: attacker.z, scale: 0.4, opacity: 0.6, time: 575 },
    { x: defender.x - 20, y: defender.y, z: defender.z, scale: 0.6, opacity: 0.3, time: 775 },
    'linear',
    'explode',
  );
};

/** PS `wingattack` → `flight` template. */
const wingattack: MoveAnim = flight;

/** PS `peck` → `contactattack` template. */
const peck: MoveAnim = contactattack;

/** PS `megapunch` → `punchattack` template. */
const megapunch: MoveAnim = punchattack;

/** PS `bite` — the jaw-snap effect plus a contact lunge. */
const bite: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  biteTemplate(scene, actors);
  contactattack(scene, actors);
};

/** Shared body for the elemental punches: a coloured orb + a fist jab (PS firepunch/icepunch/thunderpunch). */
function elementalPunch(orb: string, secondOrb: string): MoveAnim {
  return (scene: FxScene, [attacker, defender]: Actor[]) => {
    scene.showEffect(
      orb,
      { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 400 },
      {
        x: defender.leftof(-20),
        y: defender.y,
        z: defender.behind(20),
        scale: 3,
        opacity: 0,
        time: 700,
      },
      'linear',
    );
    scene.showEffect(
      secondOrb,
      { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 500 },
      {
        x: defender.leftof(-20),
        y: defender.y,
        z: defender.behind(20),
        scale: 3,
        opacity: 0,
        time: 800,
      },
      'linear',
    );
    scene.showEffect(
      'fist',
      { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 400 },
      {
        x: defender.leftof(-20),
        y: defender.y,
        z: defender.behind(20),
        scale: 2,
        opacity: 0,
        time: 800,
      },
      'linear',
    );
    attacker.anim(
      { x: defender.leftof(20), y: defender.y, z: defender.behind(-20), time: 400 },
      'ballistic2Under',
    );
    attacker.anim({ x: defender.x, y: defender.y, z: defender.z, time: 50 });
    attacker.anim({ time: 500 }, 'ballistic2');
    defender.delay(425);
    defender.anim(
      { x: defender.leftof(-20), y: defender.y, z: defender.behind(20), time: 50 },
      'swing',
    );
    defender.anim({ time: 300 }, 'swing');
  };
}

/** PS `firepunch` — two fireballs + a fist jab. */
const firepunch: MoveAnim = elementalPunch('fireball', 'fireball');
/** PS `icepunch` — two icicles + a fist jab. */
const icepunch: MoveAnim = elementalPunch('icicle', 'icicle');
/** PS `thunderpunch` — an electroball + a lightning bolt + a fist jab. */
const thunderpunch: MoveAnim = elementalPunch('electroball', 'lightning');

/** PS `absorb` → the energy-siphon drain template. */
const absorb: MoveAnim = drain;

/* ──────────────────────────────────────────────────────────────────────────
 * The custom-script table, keyed by Showdown move id (toID form).
 * ──────────────────────────────────────────────────────────────────────── */

export const SHOWDOWN_MOVE_ANIMS: Record<string, MoveAnim> = {
  thunderbolt,
  thunder,
  flamethrower,
  icebeam,
  blizzard,
  hydropump,
  surf,
  bubblebeam,
  quickattack,
  scratch,
  pound,
  bodyslam,
  gigadrain,
  psychic,
  shadowball,
  sludgebomb,
  earthquake,
  rockslide,
  hyperbeam,
  solarbeam,
  wingattack,
  peck,
  megapunch,
  bite,
  firepunch,
  icepunch,
  thunderpunch,
  absorb,
};

/* ──────────────────────────────────────────────────────────────────────────
 * Flavor table — common moves with no bespoke script, mapped to a shared
 * template + (where relevant) a projectile texture. These read as themed without
 * a hand-written sequence: a coloured `attack` wisp travels to the target, or a
 * contact lunge plays. Every texture/template referenced is vendored.
 * ──────────────────────────────────────────────────────────────────────── */

/** A reusable "projectile travels attacker → defender" using a chosen texture. */
function projectile(texture: string): MoveAnim {
  return (scene: FxScene, [attacker, defender]: Actor[]) => {
    scene.showEffect(
      texture,
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 1 },
      { x: defender.x, y: defender.y, z: defender.behind(40), scale: 1, opacity: 0.5 },
      'linear',
      'explode',
    );
  };
}

/**
 * Maps a Showdown move id (toID form) to a templated animation. Used as the
 * second resolution tier (after the custom-script table) so common moves still
 * read with flavour. The mapped value is a {@link MoveAnim} ready to play.
 */
export const SHOWDOWN_MOVE_FLAVOR: Record<string, MoveAnim> = {
  ember: projectile('fireball'),
  watergun: projectile('waterwisp'),
  razorleaf: projectile('leaf1'),
  bubble: projectile('iceball'),
  thundershock: projectile('electroball'),
  mudslap: projectile('mudwisp'),
  acid: projectile('poisonwisp'),
  rockthrow: projectile('rock1'),
  gust: projectile('feather'),
  confusion: projectile('mistball'),
  watergun2: projectile('waterwisp'),
  vinewhip: contactattack,
  tackle: contactattack,
  headbutt: contactattack,
  takedown: contactattack,
  doubleedge: contactattack,
  strength: contactattack,
  furyattack: contactattack,
  hornattack: contactattack,
  tailwhip: fastanimself,
  growl: fastanimself,
  leer: fastanimself,
  cut: slashattack,
  slash: slashattack,
  furyswipes: slashattack,
  thunderwave: projectile('electroball'),
  poisonsting: projectile('poisonwisp'),
  karatechop: punchattack,
  machpunch: punchattack,
  doublekick: contactattack,
  lowkick: contactattack,
};

/* ──────────────────────────────────────────────────────────────────────────
 * Bespoke lookup — the original 28 first-batch scripts plus every Phase-2 batch
 * the port agents produced (batch0..batch9), folded into ONE flat record so
 * resolution is a single lookup. Batches are spread in order, so on any
 * duplicate move id the later batch wins (last-wins). Batch entries are either a
 * bare {@link MoveAnim} or a {@link MoveAnimEntry} ({ anim, prepareAnim?,
 * residualAnim? }); both shapes are kept here and flattened to a plain MoveAnim
 * at resolution time (see {@link resolveAnim}).
 * ──────────────────────────────────────────────────────────────────────── */

const ALL_BESPOKE: Record<string, MoveAnim | MoveAnimEntry> = {
  ...SHOWDOWN_MOVE_ANIMS,
  ...SHOWDOWN_BATCH_0,
  ...SHOWDOWN_BATCH_1,
  ...SHOWDOWN_BATCH_2,
  ...SHOWDOWN_BATCH_3,
  ...SHOWDOWN_BATCH_4,
  ...SHOWDOWN_BATCH_5,
  ...SHOWDOWN_BATCH_6,
  ...SHOWDOWN_BATCH_7,
  ...SHOWDOWN_BATCH_8,
  ...SHOWDOWN_BATCH_9,
};

/** Flatten a bespoke entry (function or `{ anim, ... }`) to a plain MoveAnim. */
function resolveAnim(entry: MoveAnim | MoveAnimEntry): MoveAnim {
  return typeof entry === 'function' ? entry : entry.anim;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Resolution
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * Normalize a move id to a Showdown id: lowercase, strip every non-alphanumeric
 * character. So our kebab-case PokeAPI ids ("ice-beam", "hyper-beam",
 * "thunder-punch", "quick-attack") collapse to Showdown's ids ("icebeam",
 * "hyperbeam", "thunderpunch", "quickattack").
 */
export function toID(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/**
 * Resolve a {@link MoveAnim} for a move, or `null` to let the caller fall back to
 * its procedural animation. Resolution order:
 *   1. a bespoke custom script — the original {@link SHOWDOWN_MOVE_ANIMS} plus
 *      every Phase-2 batch (batch0..batch9), combined into {@link ALL_BESPOKE}
 *      with last-wins precedence; these take precedence over the flavor table;
 *   2. a flavor-table template ({@link SHOWDOWN_MOVE_FLAVOR});
 *   3. the category default template, when `category` is given
 *      (special → `attack`, physical → `contactattack`, status → `fastanimself`);
 *   4. `null` (procedural fallback).
 *
 * @param moveId    our move id (any case / separators); normalized via {@link toID}.
 * @param category  the move's category, used for the tier-3 default. Optional —
 *                  when omitted, tiers 1–2 only, then `null`.
 */
export function getShowdownAnim(moveId: string, category?: MoveCategory): MoveAnim | null {
  const id = toID(moveId);
  const bespoke = ALL_BESPOKE[id];
  if (bespoke) return resolveAnim(bespoke);
  const flavor = SHOWDOWN_MOVE_FLAVOR[id];
  if (flavor) return flavor;
  if (category) {
    switch (category) {
      case 'special':
        return attack;
      case 'physical':
        return contactattack;
      case 'status':
        return fastanimself;
    }
  }
  return null;
}
